import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties, type JSX } from 'react';
import type { ChatMessage } from '../lib/types';

export type CcMode = 'off' | 'card' | 'persist';

interface SubtitleStreamProps {
  messages: ChatMessage[];
  historyLoaded: boolean;
  onForget(note: string): void;
  ccMode: CcMode;
  /** true while the input line is open — the only time the scrollback is on screen */
  historyOpen: boolean;
}

interface Exchange {
  key: number;
  user?: ChatMessage;
  dj?: ChatMessage;
}

type Phase = 'thinking' | 'revealing' | 'hold' | 'out';

interface ActiveCard {
  key: number;
  exch: Exchange;
  phase: Phase;
  revealCount: number;
}

function isEmptyDj(m: ChatMessage): boolean {
  return m.role === 'dj' && !m.text && (!m.remembered || m.remembered.length === 0);
}

// 。！？…； end a clause; keep the terminator attached to the sentence it closes.
function splitSentences(text: string): string[] {
  const matches = text.match(/[^。！？…；]+[。！？…；]*/g);
  return matches && matches.length > 0 ? matches : [text];
}

function countChars(s?: string): number {
  return s ? [...s].length : 0;
}

function computeHoldMs(exch: Exchange): number {
  const total = countChars(exch.user?.text) + countChars(exch.dj?.text);
  return Math.min(12000, Math.max(4000, 1500 + total * 220));
}

function buildAllExchanges(messages: ChatMessage[]): Exchange[] {
  const result: Exchange[] = [];
  let open: Exchange | null = null;
  messages.forEach((m, i) => {
    if (isEmptyDj(m)) return;
    if (m.role === 'user') {
      open = { key: i, user: m };
      result.push(open);
    } else if (open && !open.dj) {
      open.dj = m;
    } else {
      open = { key: i, dj: m };
      result.push(open);
    }
  });
  return result;
}

function useEnter(): boolean {
  const [entered, setEntered] = useState(false);
  useLayoutEffect(() => {
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, []);
  return entered;
}

function cardStyle(phase: Phase, entered: boolean, reduced: boolean): CSSProperties {
  if (reduced) {
    return { opacity: phase === 'out' ? 0 : 1, transition: 'opacity .12s linear' };
  }
  if (phase === 'out') {
    return {
      opacity: 0,
      filter: 'blur(6px)',
      transform: 'translateY(-4px)',
      transition: 'opacity .4s ease, filter .4s ease, transform .4s ease',
    };
  }
  return {
    opacity: entered ? 1 : 0,
    transform: entered ? 'translateY(0)' : 'translateY(8px)',
    filter: entered ? 'blur(0px)' : 'blur(6px)',
    transition: 'opacity .18s ease, transform .18s ease, filter .2s ease',
  };
}

function UserBubble({ text, compact }: { text: string; compact?: boolean }) {
  return (
    <div className="flex w-full justify-end">
      <p
        className={`surface-2 pointer-events-auto max-w-[75%] truncate rounded-2xl border bd px-3 py-1.5 text-right text-fg ${
          compact ? 'text-[11px]' : 'text-sm'
        }`}
      >
        {text}
      </p>
    </div>
  );
}

function DjBubble({ children, compact }: { children: React.ReactNode; compact?: boolean }) {
  return (
    <div className="flex w-full justify-start">
      <div
        className={`glass pointer-events-auto font-display max-w-[85%] whitespace-pre-wrap break-words rounded-[20px] text-center text-fg/85 ${
          compact ? 'px-4 py-2 text-sm' : 'px-4 py-2.5 text-base md:text-lg'
        }`}
        style={compact ? { maxHeight: '6rem', overflow: 'hidden' } : undefined}
      >
        {children}
      </div>
    </div>
  );
}

// iMessage-style typing bubble: three pulsing dots, no text.
function TypingBubble({ reduced }: { reduced: boolean }) {
  return (
    <div className="flex w-full justify-start">
      <span
        className="glass pointer-events-auto inline-flex items-center gap-1.5 rounded-[20px] px-3.5 py-2.5"
        aria-label="Claudio 正在想"
      >
        {[0, 1, 2].map((i) => (
          <span
            key={i}
            className="h-2 w-2 rounded-full bg-fg/60"
            style={reduced ? { opacity: 0.6 } : { animation: `typingDot 1.2s ease-in-out ${i * 0.15}s infinite` }}
          />
        ))}
      </span>
    </div>
  );
}

function RememberedNotes({ notes, onForget }: { notes: string[]; onForget(note: string): void }) {
  return (
    <div className="pointer-events-auto flex w-full flex-wrap justify-center gap-1.5">
      {notes.map((note) => (
        <span
          key={note}
          className="flex max-w-[70vw] items-center gap-1.5 rounded-full border bd px-2 py-0.5 text-[11px] text-muted"
        >
          <span className="shrink-0 text-seal">记住了：</span>
          <span className="min-w-0 truncate">{note}</span>
          <button
            aria-label="忘记这条"
            onClick={() => onForget(note)}
            className="shrink-0 text-faint transition hover:text-fg"
          >
            ✕
          </button>
        </span>
      ))}
    </div>
  );
}

function PairCard({
  card,
  reduced,
  forgotten,
  onForget,
  onHoldEnter,
  onHoldLeave,
}: {
  card: ActiveCard;
  reduced: boolean;
  forgotten: Set<string>;
  onForget(note: string): void;
  onHoldEnter(key: number): void;
  onHoldLeave(key: number): void;
}) {
  const entered = useEnter();
  const { key, exch, phase, revealCount } = card;
  const sentences = exch.dj?.text ? splitSentences(exch.dj.text) : [];
  const shown = sentences.slice(0, revealCount);
  const notes = (exch.dj?.remembered ?? []).filter((n) => !forgotten.has(n));

  return (
    <div
      className="flex w-full flex-col gap-1.5"
      style={cardStyle(phase, entered, reduced)}
      onPointerEnter={(e) => e.pointerType === 'mouse' && onHoldEnter(key)}
      onPointerLeave={(e) => e.pointerType === 'mouse' && onHoldLeave(key)}
      onTouchStart={() => onHoldEnter(key)}
      onTouchEnd={() => onHoldLeave(key)}
      onTouchCancel={() => onHoldLeave(key)}
    >
      {exch.user && <UserBubble text={exch.user.text} />}
      {exch.dj ? (
        shown.length > 0 && (
          <DjBubble>
            <div className="flex flex-col items-center gap-0.5" style={{ maxHeight: '9rem', overflowY: 'auto' }}>
              {shown.map((s, i) => (
                <p key={i} className={reduced ? '' : 'sentence-in'}>
                  {s}
                </p>
              ))}
            </div>
          </DjBubble>
        )
      ) : (
        phase === 'thinking' && <TypingBubble reduced={reduced} />
      )}
      {notes.length > 0 && <RememberedNotes notes={notes} onForget={onForget} />}
    </div>
  );
}

function PersistCard({
  exch,
  forgotten,
  onForget,
}: {
  exch: Exchange;
  forgotten: Set<string>;
  onForget(note: string): void;
}) {
  const notes = (exch.dj?.remembered ?? []).filter((n) => !forgotten.has(n));
  return (
    <div className="flex w-full flex-col gap-1" style={{ opacity: 0.6 }}>
      {exch.user && <UserBubble text={exch.user.text} compact />}
      {exch.dj?.text && <DjBubble compact>{exch.dj.text}</DjBubble>}
      {notes.length > 0 && <RememberedNotes notes={notes} onForget={onForget} />}
    </div>
  );
}

export function SubtitleStream({
  messages,
  historyLoaded,
  onForget,
  ccMode,
  historyOpen,
}: SubtitleStreamProps): JSX.Element | null {
  const seen = useRef<number | null>(null);
  if (seen.current === null) seen.current = messages.length;
  // messages just before historyLoaded flips: the merge prepends history in front of
  // this same array, so its objects survive by reference and anchor the real boundary.
  const preHistory = useRef<ChatMessage[]>([]);
  if (!historyLoaded) preHistory.current = messages;

  const [active, setActive] = useState<ActiveCard[]>([]);
  const [forgotten, setForgotten] = useState<Set<string>>(new Set());
  const nextKey = useRef(0);
  const openExchange = useRef<Exchange | null>(null);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const holdKey = useRef<number | null>(null);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const holdEndAt = useRef(0);
  const holdRemaining = useRef(0);
  const hoveredKeys = useRef<Set<number>>(new Set());
  const [reduced] = useState(
    () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches,
  );

  const allExchanges = useMemo(() => buildAllExchanges(messages), [messages]);

  // stick to the newest content unless the user scrolled up to read; a genuinely new
  // message always snaps back to the latest.
  const boxRef = useRef<HTMLDivElement>(null);
  const pinnedRef = useRef(true);
  const onScroll = () => {
    const el = boxRef.current;
    if (el) pinnedRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
  };
  const lastLen = useRef(messages.length);
  useEffect(() => {
    if (messages.length > lastLen.current) pinnedRef.current = true;
    lastLen.current = messages.length;
  }, [messages.length]);
  useEffect(() => {
    const el = boxRef.current;
    if (el && pinnedRef.current) el.scrollTop = el.scrollHeight;
  });

  useEffect(() => {
    if (!historyLoaded) return;
    const prior = preHistory.current;
    let boundary = messages.length;
    if (prior.length > 0) {
      const idx = messages.indexOf(prior[0]);
      if (idx !== -1 && idx + prior.length <= messages.length) boundary = idx + prior.length;
    }
    seen.current = boundary;
  }, [historyLoaded]);

  const clearHoldTimer = () => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
  };
  const cancelHold = (forKey?: number) => {
    if (forKey !== undefined && holdKey.current !== forKey) return;
    clearHoldTimer();
    holdKey.current = null;
  };
  const expireHold = () => {
    const key = holdKey.current;
    clearHoldTimer();
    holdKey.current = null;
    if (key == null) return;
    setActive((prev) => prev.map((a) => (a.key === key ? { ...a, phase: 'out' } : a)));
  };
  const startHoldCountdown = (key: number, ms: number) => {
    clearHoldTimer();
    holdKey.current = key;
    if (hoveredKeys.current.has(key)) {
      holdRemaining.current = ms;
      return;
    }
    holdEndAt.current = Date.now() + ms;
    holdTimer.current = setTimeout(expireHold, ms);
  };
  const pauseHold = (key: number) => {
    if (holdKey.current !== key || !holdTimer.current) return;
    holdRemaining.current = Math.max(0, holdEndAt.current - Date.now());
    clearHoldTimer();
  };
  const resumeHold = (key: number) => {
    if (holdKey.current !== key || holdTimer.current) return;
    const remaining = holdRemaining.current;
    holdEndAt.current = Date.now() + remaining;
    holdTimer.current = setTimeout(expireHold, remaining);
  };
  const onCardPointerEnter = (key: number) => {
    hoveredKeys.current.add(key);
    pauseHold(key);
  };
  const onCardPointerLeave = (key: number) => {
    hoveredKeys.current.delete(key);
    resumeHold(key);
  };

  // 新交换出现时旧卡立即溶解：at most one card holds the floor.
  const dissolveAndPush = (exch: Exchange) => {
    setActive((prev) => {
      const dissolved = prev.map((a) => {
        if (a.phase === 'out') return a;
        cancelHold(a.key);
        const t = timers.current.get(a.key);
        if (t) {
          clearTimeout(t);
          timers.current.delete(a.key);
        }
        return { ...a, phase: 'out' as Phase };
      });
      return [...dissolved, { key: exch.key, exch, phase: exch.dj ? 'revealing' : 'thinking', revealCount: 0 }];
    });
  };

  useEffect(() => {
    const from = seen.current ?? messages.length;
    if (messages.length <= from) return;
    const incoming = messages.slice(from).filter((m) => !isEmptyDj(m));
    seen.current = messages.length;
    incoming.forEach((m) => {
      if (m.role === 'user') {
        const exch: Exchange = { key: nextKey.current++, user: m };
        openExchange.current = exch;
        dissolveAndPush(exch);
      } else {
        const open = openExchange.current;
        if (open && !open.dj) {
          const updated: Exchange = { ...open, dj: m };
          openExchange.current = null;
          setActive((prev) =>
            prev.map((a) => (a.key === open.key ? { ...a, exch: updated, phase: 'revealing', revealCount: 0 } : a)),
          );
        } else {
          const exch: Exchange = { key: nextKey.current++, dj: m };
          openExchange.current = null;
          dissolveAndPush(exch);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages]);

  useEffect(() => {
    active.forEach((card) => {
      if (timers.current.has(card.key)) return;
      if (card.phase === 'revealing') {
        const total = card.exch.dj?.text ? splitSentences(card.exch.dj.text).length : 0;
        if (card.revealCount >= total) {
          setActive((prev) => prev.map((a) => (a.key === card.key ? { ...a, phase: 'hold' } : a)));
          startHoldCountdown(card.key, computeHoldMs(card.exch));
          return;
        }
        const delay = reduced ? 0 : card.revealCount === 0 ? 60 : 320;
        const t = setTimeout(() => {
          timers.current.delete(card.key);
          setActive((prev) =>
            prev.map((a) => (a.key === card.key ? { ...a, revealCount: reduced ? total : a.revealCount + 1 } : a)),
          );
        }, delay);
        timers.current.set(card.key, t);
      } else if (card.phase === 'out') {
        const delay = reduced ? 0 : 420;
        const t = setTimeout(() => {
          timers.current.delete(card.key);
          setActive((prev) => prev.filter((a) => a.key !== card.key));
        }, delay);
        timers.current.set(card.key, t);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, reduced]);

  useEffect(() => {
    const map = timers.current;
    return () => {
      map.forEach(clearTimeout);
      clearHoldTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const forget = (note: string) => {
    setForgotten((prev) => (prev.has(note) ? prev : new Set(prev).add(note)));
    onForget(note);
  };

  let content: JSX.Element | null = null;
  if (ccMode === 'card') {
    content = (
      <>
        {active.map((card) => (
          <PairCard
            key={card.key}
            card={card}
            reduced={reduced}
            forgotten={forgotten}
            onForget={forget}
            onHoldEnter={onCardPointerEnter}
            onHoldLeave={onCardPointerLeave}
          />
        ))}
      </>
    );
  } else if (ccMode === 'persist') {
    content = (
      <>
        {allExchanges.slice(-2).map((exch) => (
          <PersistCard key={exch.key} exch={exch} forgotten={forgotten} onForget={forget} />
        ))}
      </>
    );
  }

  // the scrollback must never repeat what the active area is showing. Active cards carry their
  // own keys, so dedup by the underlying message objects (shared by reference with allExchanges).
  const history = useMemo(() => {
    if (ccMode === 'persist') {
      const shown = new Set(allExchanges.slice(-2).map((e) => e.key));
      return allExchanges.filter((e) => !shown.has(e.key));
    }
    const activeMsgs = new Set<ChatMessage>();
    for (const a of active) {
      if (a.exch.user) activeMsgs.add(a.exch.user);
      if (a.exch.dj) activeMsgs.add(a.exch.dj);
    }
    return allExchanges.filter(
      (e) => !(e.user && activeMsgs.has(e.user)) && !(e.dj && activeMsgs.has(e.dj)),
    );
  }, [ccMode, active, allExchanges]);

  if (ccMode === 'off') return null;

  const hasContent = allExchanges.length > 0 || active.length > 0;
  const scrollable = historyOpen && hasContent;
  return (
    <div
      ref={boxRef}
      onScroll={onScroll}
      className={`chat-clip ${scrollable ? 'pointer-events-auto' : 'pointer-events-none'} fixed inset-x-0 bottom-[calc(5rem+max(1rem,env(safe-area-inset-bottom)))] z-[6] mx-auto flex h-[38dvh] w-full max-w-xl touch-pan-y flex-col overflow-y-auto overscroll-contain px-6 md:h-[44dvh]`}
    >
      <div className="mt-auto flex w-full flex-col items-stretch gap-1.5 pt-1">
        {historyOpen &&
          history.map((exch) => (
            <PersistCard key={exch.key} exch={exch} forgotten={forgotten} onForget={forget} />
          ))}
        <div className="flex w-full flex-col items-stretch gap-2">{content}</div>
      </div>
    </div>
  );
}
